import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {firstValueFrom} from 'rxjs';
import {PrintBuilderService} from './print-builder.service';
import {BsToastService} from './bs-toast-service';

@Injectable({providedIn: 'root'})
export class InvoiceDocumentService {
  constructor(
    private data: DataService,
    private printer: PrintBuilderService,
    private toast: BsToastService,
  ) {
  }

  // Simple in-memory cache for active Terms catalog during the session
  private _termsCache: any[] | null = null;

  private async loadActiveTerms(): Promise<any[]> {
    if (Array.isArray(this._termsCache)) {
      return this._termsCache;
    }
    try {
      const res: any = await firstValueFrom(this.data.getData('term', '?state='));
      const list = (res && (res.Data ?? res)) || [];
      this._termsCache = Array.isArray(list) ? list : [];
      return this._termsCache;
    } catch {
      this._termsCache = [];
      return this._termsCache;
    }
  }

  /**
   * Print an invoice-like document (AR/AP Invoice, Quote, Sales Order, Purchase Order)
   * by its header id directly from the list without routing to the form.
   *
   * @param id header/document id
   * @param docType one of 'ar' | 'ap' | 'quotes' | 'salesorders' | 'purchaseorders'
   */
  async printById(id: string, docType?: string): Promise<void> {
    try {
      const vm = await this.buildVmForPrint(id, docType);
      const html = this.printer.buildPrintHtml(vm, vm.subLedger || vm.type || 'ar');
      this.printHtmlInIframe(html);
    } catch (e) {
      console.error('Invoice print failed', e);
      this.toast.showErrorToast($localize`:Unable to print document@@unableToPrint:Unable to print document`);
    }
  }

  /**
   * Build an EmailModel for the given document id, mirroring invoice-form.setEmailData().
   * Uses the same PrintBuilderService to generate the pdfHtml so the email and print match.
   */
  async buildEmailModelById(id: string, docType?: string): Promise<any> {
    try {
      const vm = await this.buildVmForPrint(id, docType);
      const resolvedType = String(vm.subLedger || vm.type || docType || 'ar').toLowerCase();

      // Determine human-friendly document name (match invoice-form cases)
      let emailDocumentName = 'document';
      switch (resolvedType) {
        case 'ar-credit':
          emailDocumentName = 'Credit Memo';
          break;
        case 'ap-credit':
          emailDocumentName = 'Payable Credit Memo';
          break;
        case 'ap':
          emailDocumentName = 'Payable Invoice';
          break;
        case 'ar':
          emailDocumentName = 'Receivable Invoice';
          break;
        case 'so':
        case 'salesorders':
          emailDocumentName = 'Sales Order';
          break;
        case 'sq':
        case 'quotes':
        case 'salesquotes':
          emailDocumentName = 'Sales Quote';
          break;
        case 'po':
        case 'purchaseorders':
          emailDocumentName = 'Purchase Order';
          break;
      }

      const emailDocObj: any = {};
      emailDocObj.print_orientation = '1';
      emailDocObj.convert_attachment_to = 'pdf';
      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = id;
      emailDocObj.toEmail = '';
      const number = vm?.item?.header?.number || '';
      emailDocObj.body = 'Please see the attached ' + emailDocumentName + '-' + number + '.<br><br><br>'
        + 'Best regards,'
        + '<br><br>'
        + 'AcctXErp'
        + '<br><br>';
      emailDocObj.Subject = 'AcctXErp ' + emailDocumentName + ' - ' + number;
      emailDocObj.activity_message = emailDocumentName;
      emailDocObj.invoiceNumber = number;

      try {
        emailDocObj.pdfHtml = this.printer.buildEmailHtml(vm, resolvedType);
      } catch {
        emailDocObj.pdfHtml = '';
      }
      const safeName = (emailDocumentName || 'document').replace(/\s+/g, '_');
      emailDocObj.FileName = number ? `${safeName}_${number}.pdf` : `${safeName}.pdf`;

      return emailDocObj;
    } catch (e) {
      console.error('Invoice buildEmailModelById failed', e);
      this.toast.showErrorToast($localize`:Unable to prepare email@@unableToPrepareEmail:Unable to prepare email`);
      throw e;
    }
  }

  /**
   * Load and normalize data needed for invoice printing. We mirror the shapes used
   * by invoice-form and the PrintBuilderService simple invoice layout.
   */
  private async buildVmForPrint(id: string, docType?: string): Promise<any> {
    // Load the entry first so we can deterministically resolve entity_id for address lookups
    const entry = await firstValueFrom(this.data.getData('entry', id));

    // Resolve entity_id/vendor_id from the entry payload (item.header preferred)
    let resolvedEntityId: any;
    try {
      const e: any = entry || {};
      const header = (e?.item?.header) || e?.header || {};
      resolvedEntityId = header?.entity_id
        ?? e?.entity_id
        ?? e?.vendor_id
        ?? e?.item?.entity_id
        ?? e?.item?.vendor_id
        ?? undefined;
    } catch {
      resolvedEntityId = undefined;
    }

    // Build query strings for address endpoints; include entity_id when available
    const billQuery = `?type=billing_address=true${resolvedEntityId != null ? `&entity_id=${resolvedEntityId}` : ''}`;
    const shipQuery = `?type=ship${resolvedEntityId != null ? `&entity_id=${resolvedEntityId}` : ''}`;

    // Load companies and filtered addresses in parallel
    const [companies, billTo, shipTo] = await Promise.all([
      firstValueFrom(this.data.getData('company', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r),
      firstValueFrom(this.data.getData('entity_address/addresses', billQuery)).then((r: any) => r?.Data ?? r).catch(() => []),
      firstValueFrom(this.data.getData('entity_address/addresses', shipQuery)).then((r: any) => r?.Data ?? r).catch(() => []),
    ]);

    // Build the VM expected by PrintBuilderService
    const vm: any = {
      item: entry || {},
      company: companies,
      billToAddress: billTo,
      shipToAddress: shipTo,
      subLedger: this.resolveDocType(entry, docType),
      type: this.resolveDocType(entry, docType),
    };

    // Normalize header shape and ensure bill/ship address ids are populated, especially for AP (payables)
    try {
      // Resolve a mutable header reference used by the print builder
      const hdrSrc = (entry as any) || {};
      const resolvedHeader = (hdrSrc?.item?.header)
        || (hdrSrc?.header)
        || {};

      // Ensure vm.item.header exists
      vm.item.header = vm.item.header || resolvedHeader || {};

      // Map known alternative fields onto the unified header shape
      const h = vm.item.header;
      // Number/date/company fallbacks
      h.number = h.number ?? hdrSrc?.number;
      h.header_date = h.header_date ?? hdrSrc?.header_date;
      h.company_id = h.company_id ?? hdrSrc?.company_id ?? hdrSrc?.item?.company_id;

      // Restore Terms and Due Date with robust fallbacks
      try {
        const firstNonEmpty = (...vals: any[]) => {
          for (const v of vals) {
            if (v !== undefined && v !== null) {
              const s = String(v).trim();
              if (s) {
                return s;
              }
            }
          }
          return undefined;
        };

        // Terms: consider various common field names from API payloads
        const resolvedTerms = firstNonEmpty(
          (h as any).terms,
          (h as any).terms_name,
          (h as any).terms_description,
          (h as any).terms_desc,
          (h as any).payment_terms,
          (hdrSrc as any)?.terms,
          (hdrSrc as any)?.terms_name,
          (hdrSrc as any)?.terms_description,
          (hdrSrc as any)?.item?.terms,
          (hdrSrc as any)?.item?.terms_name,
          (hdrSrc as any)?.item?.terms_description
        );
        if (resolvedTerms !== undefined) {
          (h as any).terms = resolvedTerms;
        }

        // Due Date: accept several casings/aliases
        const resolvedDueDate = ((): any => {
          const v = firstNonEmpty(
            (h as any).due_date,
            (h as any).dueDate,
            (h as any).duedate,
            (hdrSrc as any)?.due_date,
            (hdrSrc as any)?.dueDate,
            (hdrSrc as any)?.item?.due_date,
            (hdrSrc as any)?.item?.dueDate
          );
          return v;
        })();
        if (resolvedDueDate !== undefined) {
          (h as any).due_date = resolvedDueDate;
        }
      } catch { /* non-fatal */
      }

      // If Terms still not present but a term_id is available, resolve from active catalog (once) and cache
      try {
        const headerAny: any = h as any;
        const hasTermsText = !!(headerAny?.terms && String(headerAny.terms).trim());
        const termId = headerAny?.term_id ?? headerAny?.terms_id ?? headerAny?.payment_term_id;
        if (!hasTermsText && termId != null) {
          const termsList = await this.loadActiveTerms();
          // Expose catalog on VM so PrintBuilder.resolveTerm can use it as well
          vm.term = termsList;
          const found = termsList.find(t => (t?.id == termId) || (t?.term_id == termId));
          if (found && (found.name || found.description)) {
            headerAny.terms = (found.name ?? found.description);
          }
        } else if (!vm.term) {
          // Even if header already has terms text, expose catalog when available for consistency
          try {
            vm.term = await this.loadActiveTerms();
          } catch { /* ignore */
          }
        }
      } catch { /* ignore terms resolution failures */
      }

      // Entity/vendor id resolution
      const entityId = h.entity_id
        ?? hdrSrc?.entity_id
        ?? hdrSrc?.vendor_id
        ?? hdrSrc?.item?.entity_id
        ?? hdrSrc?.item?.vendor_id
        ?? undefined;
      if (entityId != null) {
        h.entity_id = h.entity_id ?? entityId;
      }

      // If vendor-specific address ids exist, map them
      if (h.vendor_bill_address_id && !h.bill_address_id) {
        h.bill_address_id = h.vendor_bill_address_id;
      }
      if (h.vendor_ship_address_id && !h.ship_address_id) {
        h.ship_address_id = h.vendor_ship_address_id;
      }

      // Deterministic fallback: if bill/ship ids are still missing, select the first address for the entity
      const billList: any[] = Array.isArray(vm.billToAddress) ? vm.billToAddress : [];
      const shipList: any[] = Array.isArray(vm.shipToAddress) ? vm.shipToAddress : [];
      if (!h.bill_address_id && entityId != null) {
        const bill = billList.find(a => String(a?.entity_id) === String(entityId));
        if (bill?.id != null) {
          h.bill_address_id = bill.id;
        }
      }
      if (!h.ship_address_id && entityId != null) {
        const ship = shipList.find(a => String(a?.entity_id) === String(entityId));
        if (ship?.id != null) {
          h.ship_address_id = ship.id;
        }
      }
    } catch {
      // non-fatal; builder will show (none selected) if still not found
    }

    return vm;
  }

  private resolveDocType(entry: any, override?: string): string {
    if (override) {
      return String(override).toLowerCase();
    }
    try {
      // Infer from batch.type_id if present; 11/12 are AR/AP in this app, but invoices can also be quotes/orders.
      const bt = entry?.batch?.type_id || entry?.item?.batchTypeId;
      if (String(bt) === '12') {
        return 'ap';
      }
      if (String(bt) === '11') {
        return 'ar';
      }
    } catch {
    }
    // Fallback
    return 'ar';
  }

  private printHtmlInIframe(html: string) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    const doPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
      } finally {
        setTimeout(() => {
          try {
            iframe.remove();
          } catch {
          }
        }, 400);
      }
    };
    iframe.onload = doPrint;
    (iframe as any).srcdoc = html;
    document.body.appendChild(iframe);
  }
}
